import React, { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from 'react';
import { useCurrentTeam } from '@/providers/TeamProvider';

type Props = {
  children: ReactNode;
};

export type PurchaseOrderSearchCondition = {
  team_purchase_order_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  payment_method_ids: number[];
  payment_context_ids: number[];
  agency_ids: number[];
  my_project: boolean;
  archived: boolean;
  page: number;
};

export const defaultPurchaseOrderSearchCondition: PurchaseOrderSearchCondition = {
  team_purchase_order_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  payment_method_ids: [],
  payment_context_ids: [],
  agency_ids: [],
  my_project: true,
  archived: false,
  page: 1,
};

export type SalesOrderSearchCondition = {
  team_sales_order_phase_ids: number[];
  user_ids: number[];
  business_partner_ids: number[];
  product_ids: number[];
  agency_ids: number[];
  my_project: boolean;
  archived: boolean;
  name: string;
  page: number;
};

export const defaultSalesOrderSearchCondition: SalesOrderSearchCondition = {
  team_sales_order_phase_ids: [],
  user_ids: [],
  business_partner_ids: [],
  product_ids: [],
  agency_ids: [],
  my_project: true,
  archived: false,
  name: '',
  page: 1,
};

type PurchaseOrderDisplayType = 'list' | 'kanban';
type SalesOrderDisplayType = 'list' | 'kanban';

/**
 * チームごとのPurchaseOrderSearchCondition
 */
type TeamPurchaseOrderSearchCondition = {
  team_id: number;
  purchaseOrderSearchCondition: PurchaseOrderSearchCondition;
};

/**
 * チームごとのSalesOrderSearchCondition
 */
type TeamSalesOrderSearchCondition = {
  team_id: number;
  salesOrderSearchCondition: SalesOrderSearchCondition;
};

export type SearchConditionContextType = {
  purchaseOrderDisplayType: PurchaseOrderDisplayType;
  setPurchaseOrderDisplayType: Dispatch<SetStateAction<PurchaseOrderDisplayType>>;
  purchaseOrderSearchCondition: PurchaseOrderSearchCondition;
  setPurchaseOrderSearchCondition: Dispatch<SetStateAction<PurchaseOrderSearchCondition>>;

  salesOrderDisplayType: SalesOrderDisplayType;
  setSalesOrderDisplayType: Dispatch<SetStateAction<SalesOrderDisplayType>>;
  salesOrderSearchCondition: SalesOrderSearchCondition;
  setSalesOrderSearchCondition: Dispatch<SetStateAction<SalesOrderSearchCondition>>;
};

export const SearchConditionContext = createContext<SearchConditionContextType>({
  purchaseOrderDisplayType: 'list',
  setPurchaseOrderDisplayType: () => {},
  purchaseOrderSearchCondition: defaultPurchaseOrderSearchCondition,
  setPurchaseOrderSearchCondition: () => {},

  salesOrderDisplayType: 'list',
  setSalesOrderDisplayType: () => {},
  salesOrderSearchCondition: defaultSalesOrderSearchCondition,
  setSalesOrderSearchCondition: () => {},
});

export const SearchConditionProvider = ({ children }: Props) => {
  const { _t_ } = useCurrentTeam();
  const [purchaseOrderDisplayType, setPurchaseOrderDisplayType] = useState<PurchaseOrderDisplayType>('list');
  const [purchaseOrderSearchCondition, setPurchaseOrderSearchCondition] = useState<PurchaseOrderSearchCondition>(
    defaultPurchaseOrderSearchCondition
  );
  const [teamPurchaseOrderSearchConditions, setTeamPurchaseOrderSearchConditions] = useState<
    TeamPurchaseOrderSearchCondition[]
  >([]);

  const [salesOrderDisplayType, setSalesOrderDisplayType] = useState<SalesOrderDisplayType>('list');
  const [salesOrderSearchCondition, setSalesOrderSearchCondition] = useState<SalesOrderSearchCondition>(
    defaultSalesOrderSearchCondition
  );
  const [teamSalesOrderConditions, setTeamSalesOrderConditions] = useState<TeamSalesOrderSearchCondition[]>([]);

  useEffect(() => {
    if (_t_) {
      const teamPurchaseCondition = teamPurchaseOrderSearchConditions.find((c) => c.team_id == _t_.id);
      if (teamPurchaseCondition == null) {
        const c = defaultPurchaseOrderSearchCondition;

        setPurchaseOrderSearchCondition(c);
        setTeamPurchaseOrderSearchConditions((prevCondition) => [
          ...prevCondition,
          {
            team_id: _t_.id,
            purchaseOrderSearchCondition: c,
          },
        ]);
      } else {
        setPurchaseOrderSearchCondition(teamPurchaseCondition.purchaseOrderSearchCondition);
      }

      const teamSalesCondition = teamSalesOrderConditions.find((c) => c.team_id == _t_.id);
      if (teamSalesCondition == null) {
        const c = defaultSalesOrderSearchCondition;
        setSalesOrderSearchCondition(c);
        setTeamSalesOrderConditions((prevCondition) => [
          ...prevCondition,
          {
            team_id: _t_.id,
            salesOrderSearchCondition: c,
          },
        ]);
      } else {
        setSalesOrderSearchCondition(teamSalesCondition.salesOrderSearchCondition);
      }
    }
  }, [_t_]);

  useEffect(() => {
    if (_t_) {
      const teamCondition = teamPurchaseOrderSearchConditions.find((c) => c.team_id == _t_.id);
      if (teamCondition) {
        const conditions = teamPurchaseOrderSearchConditions.map((c) =>
          c.team_id == _t_.id ? { team_id: _t_.id, purchaseOrderSearchCondition: purchaseOrderSearchCondition } : c
        );
        setTeamPurchaseOrderSearchConditions((_) => conditions);
      }
    }
  }, [purchaseOrderSearchCondition]);

  useEffect(() => {
    if (_t_) {
      const teamCondition = teamSalesOrderConditions.find((c) => c.team_id == _t_.id);
      if (teamCondition) {
        const conditions = teamSalesOrderConditions.map((c) =>
          c.team_id == _t_.id ? { team_id: _t_.id, salesOrderSearchCondition: salesOrderSearchCondition } : c
        );
        setTeamSalesOrderConditions((_) => conditions);
      }
    }
  }, [salesOrderSearchCondition]);

  return (
    <SearchConditionContext.Provider
      value={{
        purchaseOrderDisplayType,
        setPurchaseOrderDisplayType,
        purchaseOrderSearchCondition,
        setPurchaseOrderSearchCondition,
        salesOrderDisplayType,
        setSalesOrderDisplayType,
        salesOrderSearchCondition,
        setSalesOrderSearchCondition,
      }}
    >
      {children}
    </SearchConditionContext.Provider>
  );
};

export const useSearchCondition = () => {
  return useContext(SearchConditionContext);
};
